import { ReactElement } from 'react';
import {
  Field,
  FieldProps,
  Form,
  Formik,
  FormikHelpers,
  FormikProps,
  FormikValues
} from 'formik';
import * as Yup from 'yup';
import { InStockNotificationRequest } from '../../../types/generated';
import Loader from '../../../components/Loader';
import TextInput from '../../../components/TextInput';
import { registerForInStockNotification } from '../productPageApi';
import Modal from '../../../components/Modal';
import popupAlert from '../../../utilities/popup-alert';
import Icon from '../../../components/Icon';

type FormValues = Partial<InStockNotificationRequest>;

const validationSchema = Yup.object().shape({
  firstName: Yup.string().label('First Name').required(),
  lastName: Yup.string().label('Last Name').required(),
  emailAddress: Yup.string().label('Email Address').email().required()
});

interface OutOfStockFormProps {
  catalogueRef: string | null;
  showModal: boolean;
  handleClose: () => void;
}

export default function OutOfStockForm({
  catalogueRef,
  showModal,
  handleClose
}: OutOfStockFormProps): ReactElement {
  const initialValues: FormValues = {
    firstName: '',
    lastName: '',
    emailAddress: '',
    catalogueRef
  };

  function handleSubmit(values: FormikValues, errorCallback: () => void) {
    const form: FormValues = {
      ...values
    };

    registerForInStockNotification(form)
      .then((data) => {
        if (data.success) {
          // if change successful
          handleClose();
          popupAlert.showPositive('Your details have been sent successfully');
        } else {
          // if change unsuccessful
          errorCallback();
          popupAlert.showNegative(
            'There was a problem submitting your details, please try again'
          );
        }
      })
      .catch((err: Error) => {
        errorCallback();
        popupAlert.showNegative(
          'There was a problem submitting your details, please try again'
        );
        console.error(err.message);
      });
  }

  return (
    <Modal
      title="Out of stock"
      isVisible={showModal}
      handleClose={handleClose}
      escapeToClose={true}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnBlur={false}
        onSubmit={(
          values: FormValues,
          { setSubmitting }: FormikHelpers<FormValues>
        ) => {
          handleSubmit(values, () => {
            setSubmitting(false);
          });
        }}
      >
        {(formikBag: FormikProps<FormikValues>) =>
          formikBag.isSubmitting ? (
            <Loader cssModifiers="my-3" size="small" />
          ) : (
            <>
              <button
                onClick={handleClose}
                className="btn btn-close"
                aria-label="Close"
              >
                <Icon iconName="close-thin" />
              </button>
              <p>
                Please provide us with your name and email address and we will
                send you an email to let you know when this item is back in
                stock.
              </p>
              <Form noValidate={true}>
                <Field name="firstName">
                  {({
                    field,
                    form: { touched, errors }
                  }: FieldProps<string, FormValues>) => (
                    <TextInput
                      id={field.name}
                      required={true}
                      type="text"
                      label="First Name"
                      placeholder="Enter First Name"
                      autoFocus={true}
                      labelCols={{ sm: 4 }}
                      inputCols={{ sm: 8 }}
                      error={!!(touched.firstName && errors.firstName)}
                      helpText={touched.firstName && errors.firstName}
                      {...field}
                    />
                  )}
                </Field>
                <Field name="lastName">
                  {({
                    field,
                    form: { touched, errors }
                  }: FieldProps<string, FormValues>) => (
                    <TextInput
                      id={field.name}
                      required={true}
                      type="text"
                      label="Last Name"
                      placeholder="Enter Last Name"
                      labelCols={{ sm: 4 }}
                      inputCols={{ sm: 8 }}
                      error={!!(touched.lastName && errors.lastName)}
                      helpText={touched.lastName && errors.lastName}
                      {...field}
                    />
                  )}
                </Field>
                <Field name="emailAddress">
                  {({
                    field,
                    form: { touched, errors }
                  }: FieldProps<string, FormValues>) => (
                    <TextInput
                      id={field.name}
                      required={true}
                      type="email"
                      label="Email Address"
                      placeholder="Enter Email Address"
                      labelCols={{ sm: 4 }}
                      inputCols={{ sm: 8 }}
                      error={!!(touched.emailAddress && errors.emailAddress)}
                      helpText={touched.emailAddress && errors.emailAddress}
                      {...field}
                    />
                  )}
                </Field>
                <div className="d-flex justify-content-end">
                  <div className="form-group mt-3">
                    <button
                      type="button"
                      className="btn btn-default"
                      onClick={handleClose}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary ml-2">
                      Submit
                    </button>
                  </div>
                </div>
              </Form>
            </>
          )
        }
      </Formik>
    </Modal>
  );
}
